/**
 * generateVideoThumbnail — Captures the first frame of a video file as a WebP thumbnail.
 *
 * Loads the file into a hidden <video>, seeks to 0.5s (to skip black frames),
 * draws the frame to a canvas, and returns a WebP Blob.
 */

const THUMB_MAX_WIDTH = 320;
const THUMB_MAX_HEIGHT = 320;
const SEEK_TIME = 0.5;

export async function generateVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const objectUrl = URL.createObjectURL(file);
    let resolved = false;

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
    };

    const fail = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };

    // Timeout after 8 seconds
    const timeout = setTimeout(fail, 8000);

    video.addEventListener('error', fail);

    video.addEventListener('loadedmetadata', () => {
      // Seek to 0.5s or beginning if video is shorter
      video.currentTime = Math.min(SEEK_TIME, video.duration);
    });

    video.addEventListener('seeked', () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeout);

      try {
        // Scale down while maintaining aspect ratio
        let w = video.videoWidth;
        let h = video.videoHeight;

        if (w > THUMB_MAX_WIDTH) {
          h = Math.round(h * (THUMB_MAX_WIDTH / w));
          w = THUMB_MAX_WIDTH;
        }
        if (h > THUMB_MAX_HEIGHT) {
          w = Math.round(w * (THUMB_MAX_HEIGHT / h));
          h = THUMB_MAX_HEIGHT;
        }

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          resolve(null);
          return;
        }

        ctx.drawImage(video, 0, 0, w, h);

        canvas.toBlob(
          (blob) => {
            cleanup();
            resolve(blob);
          },
          'image/webp',
          0.7
        );
      } catch {
        cleanup();
        resolve(null);
      }
    });

    video.src = objectUrl;
  });
}
